import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Card,
  Segmented,
  Button,
  DatePicker,
  Select,
  Spin,
  Empty,
  Badge,
  Space,
  Tooltip,
  message,
} from 'antd';
import {
  LeftOutlined,
  RightOutlined,
  ReloadOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import {
  frontDeskService,
  adminRoomService,
  adminBookingService,
  offersService,
  adminOperationsService,
} from '../../services/adminService';
import {
  AvailabilityCalendarRow,
  AvailabilityCalendarReservation,
  FrontDeskBooking,
  Offer,
} from '../../types';
import { ViewBookingModal } from '../frontdesk/components/ViewBookingModal';
import {
  BOOKING_STATUS_COLORS,
  BOOKING_STATUS_LABELS,
  BOOKING_STATUS_OPTIONS,
  ROOM_STATUS_COLORS,
  getBookingStatusColor,
} from '../../constants/bookingStatus';
import './ReservationCalendar.css';

type ViewKey = 'day' | '7' | '14' | '30';

const VIEW_DAYS: Record<ViewKey, number> = { day: 1, '7': 7, '14': 14, '30': 30 };

interface LaneItem extends AvailabilityCalendarReservation {
  s: Dayjs;
  e: Dayjs;
  offset: number;
  span: number;
  lane: number;
}

interface UnifiedEvent {
  id: string | number;
  title: string;
  source: string;
  status?: string;
  reference?: string;
  start_date: string;
  end_date: string;
}

interface ReservationCalendarProps {
  showEvents?: boolean;
}

const packLanes = (
  reservations: AvailabilityCalendarReservation[],
  rangeStart: Dayjs,
  rangeEnd: Dayjs
): LaneItem[] => {
  const items = reservations
    .map((r) => ({
      res: r,
      s: dayjs(r.check_in_date).startOf('day'),
      e: dayjs(r.check_out_date).startOf('day'),
    }))
    .filter((i) => i.e.isAfter(rangeStart) && i.s.isBefore(rangeEnd))
    .sort((a, b) => a.s.valueOf() - b.s.valueOf() || a.e.valueOf() - b.e.valueOf());

  const laneEnds: Dayjs[] = [];
  return items.map(({ res, s, e }) => {
    let lane = laneEnds.findIndex((laneEnd) => !s.isBefore(laneEnd));
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(e);
    } else {
      laneEnds[lane] = e;
    }
    const clippedStart = s.isBefore(rangeStart) ? rangeStart : s;
    const clippedEnd = e.isAfter(rangeEnd) ? rangeEnd : e;
    return {
      ...res,
      s,
      e,
      lane,
      offset: clippedStart.diff(rangeStart, 'day'),
      span: Math.max(1, clippedEnd.diff(clippedStart, 'day')),
    };
  });
};

const ReservationCalendar: React.FC<ReservationCalendarProps> = ({ showEvents }) => {
  const [loading, setLoading] = useState(false);
  const [rooms, setRooms] = useState<AvailabilityCalendarRow[]>([]);
  const [view, setView] = useState<ViewKey>('7');
  const [startDate, setStartDate] = useState<Dayjs>(() => dayjs().startOf('day'));
  const [roomTypeId, setRoomTypeId] = useState<string | undefined>(undefined);
  const [roomTypes, setRoomTypes] = useState<any[]>([]);
  const [events, setEvents] = useState<UnifiedEvent[]>([]);

  const [modalBooking, setModalBooking] = useState<FrontDeskBooking | null>(null);
  const [openingBooking, setOpeningBooking] = useState(false);
  const metaRef = useRef<{ roomTypes: any[]; offers: Offer[] } | null>(null);

  const days = VIEW_DAYS[view];
  const rangeEnd = useMemo(() => startDate.add(days, 'day'), [startDate, days]);

  const dates = useMemo(() => {
    const arr: Dayjs[] = [];
    for (let i = 0; i < days; i++) arr.push(startDate.add(i, 'day'));
    return arr;
  }, [startDate, days]);

  const fetchRoomTypes = useCallback(async () => {
    try {
      const res: any = await adminRoomService.getAllRoomTypes();
      const list = res?.data?.data || res?.data || [];
      if (Array.isArray(list)) setRoomTypes(list);
    } catch (error) {
      console.error('Error fetching room types:', error);
    }
  }, []);

  const fetchAvailability = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await frontDeskService.getAvailabilityCalendar({
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: rangeEnd.subtract(1, 'day').format('YYYY-MM-DD'),
        roomTypeId,
      });
      if (Array.isArray(res?.data)) setRooms(res.data);
      else setRooms([]);
    } catch (error) {
      console.error('Error fetching availability calendar:', error);
    } finally {
      setLoading(false);
    }
  }, [startDate, rangeEnd, roomTypeId]);

  const fetchEvents = useCallback(async () => {
    if (!showEvents) return;
    try {
      const res: any = await adminOperationsService.getUnifiedCalendar({
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: rangeEnd.subtract(1, 'day').format('YYYY-MM-DD'),
      });
      setEvents(Array.isArray(res?.data) ? res.data : []);
    } catch (error) {
      console.error('Error fetching unified calendar:', error);
    }
  }, [showEvents, startDate, rangeEnd]);

  useEffect(() => {
    void fetchRoomTypes();
  }, [fetchRoomTypes]);

  useEffect(() => {
    void fetchAvailability();
    void fetchEvents();
  }, [fetchAvailability, fetchEvents]);

  const handleOpenBooking = useCallback(async (bookingId: string) => {
    if (!bookingId) return;
    setOpeningBooking(true);
    try {
      const bookingRes: any = await adminBookingService.getBooking(bookingId);
      const booking = bookingRes?.data;
      if (!booking) {
        message.error('Booking not found');
        return;
      }
      if (!metaRef.current) {
        const [rtRes, offRes]: any[] = await Promise.all([
          adminRoomService.getAllRoomTypes(),
          offersService.getOffers(),
        ]);
        metaRef.current = {
          roomTypes: rtRes?.data?.data || rtRes?.data || [],
          offers: Array.isArray(offRes?.data) ? offRes.data : [],
        };
      }
      setModalBooking(booking);
    } catch (error) {
      console.error('Error opening booking:', error);
      message.error('Failed to load booking');
    } finally {
      setOpeningBooking(false);
    }
  }, []);

  const gridTemplate = `220px repeat(${days}, minmax(46px, 1fr))`;

  const renderRoomRow = (room: AvailabilityCalendarRow) => {
    const lanes = packLanes(room.reservations || [], startDate, rangeEnd);
    const laneCount = Math.max(1, ...lanes.map((l) => l.lane + 1));
    const roomStatusColor = ROOM_STATUS_COLORS[room.room_status] || '#8c8c8c';

    return (
      <div className="rc-room" key={room.id}>
        <div className="rc-room-label">
          <span className="rc-room-number">{room.room_number}</span>
          <span className="rc-room-type">{room.room_type_name}</span>
          <span className="rc-room-status" style={{ color: roomStatusColor }}>
            <i className="rc-status-dot" style={{ background: roomStatusColor }} />
            {room.room_status}
          </span>
        </div>
        <div
          className="rc-room-track"
          style={{
            gridTemplateColumns: `repeat(${days}, minmax(46px, 1fr))`,
            gridTemplateRows: `repeat(${laneCount}, 26px)`,
          }}
        >
          {dates.map((date, idx) => {
            const isToday = date.isSame(dayjs(), 'day');
            const isWeekend = date.day() === 0 || date.day() === 6;
            return (
              <div
                key={date.format('YYYY-MM-DD')}
                className={`rc-cell ${isToday ? 'rc-cell-today' : ''} ${
                  isWeekend ? 'rc-cell-weekend' : ''
                }`}
                style={{ gridColumn: idx + 1, gridRow: '1 / -1' }}
              />
            );
          })}
          {lanes.map((bar, idx) => (
            <Tooltip
              key={`${bar.booking_id}-${idx}`}
              title={
                <div>
                  <strong>{bar.guest_name}</strong>
                  <br />
                  {dayjs(bar.check_in_date).format('MMM DD')} -{' '}
                  {dayjs(bar.check_out_date).format('MMM DD')}
                  <br />
                  Status: {BOOKING_STATUS_LABELS[bar.status] || bar.status}
                  <br />
                  Conf: {bar.confirmation_number}
                  <br />
                  <em>Click to view booking</em>
                </div>
              }
            >
              <button
                type="button"
                className="rc-bar"
                style={{
                  gridColumn: `${bar.offset + 1} / span ${bar.span}`,
                  gridRow: bar.lane + 1,
                  background: getBookingStatusColor(bar.status),
                }}
                onClick={() => void handleOpenBooking(bar.booking_id)}
              >
                <span className="rc-bar-label">{bar.guest_name}</span>
              </button>
            </Tooltip>
          ))}
        </div>
      </div>
    );
  };

  const renderEvents = () => {
    if (!showEvents) return null;
    const visible = events.filter((event) => {
      const s = dayjs(event.start_date);
      const e = dayjs(event.end_date);
      return s.isBefore(rangeEnd) && e.isAfter(startDate);
    });
    return (
      <Card className="rc-events-card" size="small" title="Unified Events in Range">
        {visible.length === 0 ? (
          <Empty description="No events in this range" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <ul className="rc-events-list">
            {visible.map((event) => {
              let color = '#8c8c8c';
              if (event.source === 'room_booking') color = '#52c41a';
              else if (event.source === 'restaurant_reservation') color = '#1890ff';
              else if (event.source === 'sports_reservation') color = '#faad14';
              return (
                <li key={String(event.id)}>
                  <Badge
                    color={color}
                    text={
                      <span className="rc-event-text">
                        <strong>{event.title}</strong>{' '}
                        {dayjs(event.start_date).format('MMM DD')} -{' '}
                        {dayjs(event.end_date).format('MMM DD')} (
                        {event.source === 'room_booking'
                          ? event.status
                          : event.reference || event.source}
                        )
                      </span>
                    }
                  />
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    );
  };

  return (
    <div className="rc-container">
      <Card className="rc-controls" size="small">
        <div className="rc-controls-row">
          <Space wrap>
            <Segmented
              value={view}
              onChange={(value) => setView(value as ViewKey)}
              options={[
                { label: 'Day', value: 'day' },
                { label: '7 Days', value: '7' },
                { label: '14 Days', value: '14' },
                { label: '30 Days', value: '30' },
              ]}
            />
            <Button icon={<LeftOutlined />} onClick={() => setStartDate((p) => p.subtract(1, 'day'))} />
            <Button onClick={() => setStartDate(dayjs().startOf('day'))}>Today</Button>
            <Button icon={<RightOutlined />} onClick={() => setStartDate((p) => p.add(1, 'day'))} />
            <Button
              icon={<LeftOutlined />}
              onClick={() => setStartDate((p) => p.subtract(7, 'day'))}
            >
              7d
            </Button>
            <Button onClick={() => setStartDate((p) => p.add(7, 'day'))}>
              7d <RightOutlined />
            </Button>
            <DatePicker
              value={startDate}
              allowClear={false}
              onChange={(value) => value && setStartDate(value.startOf('day'))}
            />
          </Space>
          <Space wrap>
            <Select
              placeholder="Filter by Room Type"
              style={{ width: 200 }}
              allowClear
              showSearch
              optionFilterProp="label"
              value={roomTypeId}
              onChange={(value) => setRoomTypeId(value)}
              options={roomTypes.map((rt) => ({ value: rt.id, label: rt.name }))}
            />
            <Button icon={<ReloadOutlined />} onClick={() => { void fetchAvailability(); void fetchEvents(); }}>
              Refresh
            </Button>
          </Space>
        </div>
        <div className="rc-legend">
          {BOOKING_STATUS_OPTIONS.filter(
            (o) => !['checked_out', 'cancelled'].includes(o.value)
          ).map((option) => (
            <Badge key={option.value} color={BOOKING_STATUS_COLORS[option.value]} text={option.label} />
          ))}
        </div>
      </Card>

      <div className="rc-grid-wrapper">
        <Spin spinning={loading || openingBooking}>
          <div className="rc-scroll">
            <div className="rc-header" style={{ gridTemplateColumns: gridTemplate }}>
              <div className="rc-header-cell rc-sticky-label">
                <CalendarOutlined /> Room / Date
              </div>
              {dates.map((date) => {
                const isToday = date.isSame(dayjs(), 'day');
                return (
                  <div
                    key={date.format('YYYY-MM-DD')}
                    className={`rc-header-cell ${isToday ? 'rc-cell-today' : ''}`}
                  >
                    <span className="rc-header-dow">{date.format('ddd')}</span>
                    <span className="rc-header-day">{date.format('DD')}</span>
                    <span className="rc-header-month">{date.format('MMM')}</span>
                  </div>
                );
              })}
            </div>
            {rooms.length === 0 && !loading ? (
              <Empty description="No rooms found" />
            ) : (
              <div className="rc-body">
                {rooms.map((room) => renderRoomRow(room))}
              </div>
            )}
          </div>
        </Spin>
      </div>

      {renderEvents()}

      <ViewBookingModal
        open={!!modalBooking}
        onCancel={() => setModalBooking(null)}
        booking={modalBooking as any}
        roomTypes={metaRef.current?.roomTypes || []}
        offers={metaRef.current?.offers || []}
        onEdit={() => message.info('Please edit bookings from the Front Desk tab.')}
        onDelete={() => message.info('Please delete bookings from the Front Desk tab.')}
      />
    </div>
  );
};

export default ReservationCalendar;
